/**
 * Экран «Переходим к оплате» — общий шлюз перед уходом на страницу Т-Банка (#1179).
 *
 * Зачем: на странице оплаты Т-Банка (pay.tbank-online.com) нет кнопки «вернуться
 * на сайт» до оплаты, а в iOS Safari стрелка «Назад» там неактивна — страница
 * открывается без истории. Человек, нажавший «Купить» по ошибке, оказывался
 * заперт на чекауте. Поэтому ВСЕ точки оплаты (подписка, супер симпатии, разбор,
 * обновление разбора, подарок, размещение места компании) идут через этот экран:
 * что покупается, сколько стоит, «Перейти к оплате» и «Отмена».
 *
 * Использование (по образцу confirmDialog):
 *
 *     const outcome = await startPaymentHandoff({
 *       title: "Премиум подписка · 1 месяц",
 *       priceLabel: "499 ₽",
 *       createPayment: async () => {
 *         const r = await initTbankPayment(...);
 *         if (r.error || !r.data?.paymentUrl) { toast.error(...); return null; }
 *         return r.data;
 *       },
 *       onBeforeRedirect: (p) => saveSnapshot(p.paymentId),
 *     });
 *
 * Правила:
 * - Платёж создаётся только по «Перейти к оплате». «Отмена», Esc, крестик и тап по
 *   затемнению закрывают экран и оставляют человека там, откуда он начал (модалка
 *   тарифов/магазина под экраном не трогается) — висящий pending-платёж не
 *   появляется.
 * - Если «Отмена» нажата, пока платёж создаётся, перехода не будет.
 * - Переход — в ТЕКУЩЕЙ вкладке (location.assign). window.open(url, "_blank",
 *   "noopener") по спецификации HTML всегда возвращает null, поэтому прежний код
 *   «новая вкладка, иначе текущая» открывал чекаут дважды или оставлял пустую
 *   вкладку; в Android-обёртке window.open вообще тупик (MainActivity не
 *   переопределяет onCreateWindow). Возврат — как и раньше: SuccessURL/FailURL →
 *   /payment/confirm и снапшот rl.payment-resume (routing/paymentResume.ts).
 * - createPayment сам показывает свои ошибки (тост) и возвращает null — экран
 *   просто закрывается.
 *
 * В корне приложения должен быть смонтирован `<PaymentHandoffHost />`.
 */
import {AnimatePresence, motion, useReducedMotion} from "motion/react";
import {CreditCard, Loader2, X} from "lucide-react";
import {useCallback, useEffect, useRef, useState} from "react";
import {toast} from "sonner";

import {Portal} from "./Portal";

export interface PaymentHandoffPayment {
    paymentUrl: string;
    paymentId?: string;
}

export interface PaymentHandoffOptions {
    /** Что оплачивается — простыми словами: «Премиум подписка · 3 месяца». */
    title: string;
    /** Сумма к оплате строкой: «499 ₽». */
    priceLabel: string;
    /** Необязательное пояснение к покупке (например, для обновления разбора). */
    note?: string;
    /**
     * Создать платёж. Вызывается только после «Перейти к оплате».
     * null — платёж не создан (ошибку вызывающий уже показал сам).
     */
    createPayment: () => Promise<PaymentHandoffPayment | null>;
    /** Сразу перед уходом на страницу оплаты: снапшот возврата и т.п. */
    onBeforeRedirect?: (payment: PaymentHandoffPayment) => void;
}

export type PaymentHandoffOutcome = "redirected" | "cancelled" | "failed";

interface PendingHandoff extends PaymentHandoffOptions {
    resolve: (outcome: PaymentHandoffOutcome) => void;
}

let openHandoffRef: ((opts: PendingHandoff) => void) | null = null;

/** Уход на внешнюю страницу оплаты. Отдельной функцией — одна точка на всё приложение. */
export function redirectToPayment(paymentUrl: string): void {
    window.location.assign(paymentUrl);
}

export function startPaymentHandoff(opts: PaymentHandoffOptions): Promise<PaymentHandoffOutcome> {
    return new Promise((resolve) => {
        if (openHandoffRef) {
            openHandoffRef({...opts, resolve});
            return;
        }
        // Хост не смонтирован — деградируем до системного confirm, но отмену всё равно даём.
        void (async () => {
            const ok = typeof window !== "undefined"
                && window.confirm(`Переходим к оплате: ${opts.title} — ${opts.priceLabel}. Продолжить?`);
            if (!ok) return resolve("cancelled");
            const payment = await opts.createPayment();
            if (!payment) return resolve("failed");
            opts.onBeforeRedirect?.(payment);
            redirectToPayment(payment.paymentUrl);
            resolve("redirected");
        })();
    });
}

export interface PaymentHandoffViewProps {
    open: boolean;
    title: string;
    priceLabel: string;
    note?: string;
    busy: boolean;
    onContinue: () => void;
    onCancel: () => void;
}

/** Разметка экрана. Тот же язык, что у ConfirmDialog: .rla-dialog + .rla-mark + пилюли. */
export function PaymentHandoffView({open, title, priceLabel, note, busy, onContinue, onCancel}: PaymentHandoffViewProps) {
    const reduceMotion = useReducedMotion();

    // Esc → Отмена. Фаза перехвата + stopPropagation: иначе тот же Escape закрыл бы
    // и модалку тарифов под экраном (см. тот же приём в ConfirmDialogView).
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            onCancel();
        };
        window.addEventListener("keydown", handler, true);
        return () => window.removeEventListener("keydown", handler, true);
    }, [open, onCancel]);

    return (
        <Portal>
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="payment-handoff"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.18}}
                        /* z-[410]: над всеми модалками оплаты (воронка анализа — ModalShell
                           zLayer 300–304) и над ConfirmDialog (z-[400]). */
                        className="rla-dialog-scrim z-[410]"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Переходим к оплате"
                        data-testid="payment-handoff"
                        onClick={(e) => {
                            // Portal уносит узел в body, но события React всплывают по
                            // дереву компонентов — не даём клику дойти до модалки под нами.
                            e.stopPropagation();
                            onCancel();
                        }}
                    >
                        <motion.div
                            initial={{scale: reduceMotion ? 1 : 0.94, opacity: 0}}
                            animate={{scale: 1, opacity: 1}}
                            exit={{scale: reduceMotion ? 1 : 0.94, opacity: 0}}
                            transition={reduceMotion ? {duration: 0.15} : {type: "spring", bounce: 0, duration: 0.32}}
                            className="rla-dialog"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                aria-label="Закрыть"
                                className="rla-iconbtn rla-dialog-close"
                                onClick={onCancel}
                            >
                                <X className="size-[18px]"/>
                            </button>

                            <span className="rla-mark" aria-hidden="true">
                                <CreditCard className="size-[26px]" strokeWidth={2.1}/>
                            </span>
                            <h3 className="rla-dialog-title">Переходим к оплате</h3>

                            <div className="rla-pay-summary" data-testid="payment-handoff-summary">
                                <span className="rla-pay-summary-title">{title}</span>
                                <span className="rla-pay-summary-price">{priceLabel}</span>
                            </div>

                            {note && <p className="rla-dialog-text">{note}</p>}
                            <p className="rla-dialog-text">
                                Оплата пройдёт на странице Т-Банка. Вернуться с неё до оплаты
                                не получится — если передумали, нажмите «Отмена».
                            </p>

                            <div className="rla-dialog-actions">
                                <button
                                    type="button"
                                    autoFocus
                                    className="rla-btn rla-btn-cta rla-btn-full"
                                    disabled={busy}
                                    aria-busy={busy}
                                    onClick={onContinue}
                                >
                                    {busy ? (
                                        <>
                                            <Loader2 className="size-[18px] animate-spin" aria-hidden="true"/>
                                            Открываем оплату…
                                        </>
                                    ) : (
                                        "Перейти к оплате"
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="rla-btn rla-btn-line rla-btn-full"
                                    onClick={onCancel}
                                >
                                    Отмена
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Portal>
    );
}

/** Хост экрана. Монтируется один раз в корне приложения (App.tsx). */
export function PaymentHandoffHost() {
    const [pending, setPending] = useState<PendingHandoff | null>(null);
    const [busy, setBusy] = useState(false);
    const pendingRef = useRef<PendingHandoff | null>(null);

    const settle = useCallback((outcome: PaymentHandoffOutcome) => {
        pendingRef.current?.resolve(outcome);
        pendingRef.current = null;
        setPending(null);
        setBusy(false);
    }, []);

    useEffect(() => {
        openHandoffRef = (opts) => {
            // Новый запрос поверх старого — старый считается отменённым.
            pendingRef.current?.resolve("cancelled");
            pendingRef.current = opts;
            setBusy(false);
            setPending(opts);
        };
        return () => {
            openHandoffRef = null;
        };
    }, []);

    // Возврат «Назад» со страницы Т-Банка на десктопе может поднять эту страницу из
    // bfcache вместе с открытым экраном в состоянии «Открываем оплату…». Закрываем его.
    useEffect(() => {
        const onPageShow = (e: PageTransitionEvent) => {
            if (e.persisted && pendingRef.current) settle("cancelled");
        };
        window.addEventListener("pageshow", onPageShow);
        return () => window.removeEventListener("pageshow", onPageShow);
    }, [settle]);

    const cancel = useCallback(() => settle("cancelled"), [settle]);

    const proceed = useCallback(() => {
        const current = pendingRef.current;
        if (!current || busy) return;
        setBusy(true);
        void (async () => {
            let payment: PaymentHandoffPayment | null = null;
            try {
                payment = await current.createPayment();
            } catch {
                toast.error("Не удалось создать платёж");
            }
            // Пока платёж создавался, человек нажал «Отмена» (или открыл другой экран
            // оплаты) — никуда не уводим.
            if (pendingRef.current !== current) return;
            if (!payment?.paymentUrl) {
                settle("failed");
                return;
            }
            current.onBeforeRedirect?.(payment);
            // Экран остаётся открытым со спиннером, пока браузер уходит со страницы.
            current.resolve("redirected");
            redirectToPayment(payment.paymentUrl);
            // Страница так и не выгрузилась (навигацию отменили/перехватили) —
            // не оставляем вечный спиннер поверх приложения.
            window.setTimeout(() => {
                if (pendingRef.current === current) settle("redirected");
            }, 5000);
        })();
    }, [busy, settle]);

    return (
        <PaymentHandoffView
            open={!!pending}
            title={pending?.title ?? ""}
            priceLabel={pending?.priceLabel ?? ""}
            note={pending?.note}
            busy={busy}
            onContinue={proceed}
            onCancel={cancel}
        />
    );
}
